import axios from "axios";

// Vacío = misma dirección desde la que se abrió la web (el nginx del frontend reenvía /api al backend).
// Así el mismo build sirve en cualquier dominio (zebra, zebra2…) sin recompilar.
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const API = `${BACKEND_URL}/api`;

/** Evento global: el servidor respondió 401 (sesión vencida o sin iniciar). LoginGate lo escucha. */
export const UNAUTHORIZED_EVENT = "zebralab:unauthorized";

export const api = axios.create({
    baseURL: API,
    withCredentials: true, // cookie de sesión (también en desarrollo, con el API en otro puerto)
});

api.interceptors.response.use(
    (res) => res,
    (error) => {
        const url = error?.config?.url || "";
        if (error?.response?.status === 401 && !url.startsWith("/auth/") && !url.startsWith("/client-errors")) {
            window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
        }
        return Promise.reject(error);
    }
);

// ----- Acceso con contraseña -----
export async function authMe() {
    const { data } = await api.get("/auth/me");
    return data; // { authRequired, configured, authenticated }
}

export async function authLogin(password) {
    const { data } = await api.post("/auth/login", { password });
    return data;
}

export async function authLogout() {
    await api.post("/auth/logout");
}

/**
 * ZPL de un diseno. `quantity` = cantidad EXACTA de etiquetas (no de hojas): en papel
 * multi-up el backend reparte en hojas y deja vacias las celdas sobrantes.
 * Sin `quantity` devuelve una hoja completa (comportamiento clasico).
 */
export async function generateZpl(design, substitutions = null, quantity = null) {
    const body = { design, substitutions };
    if (quantity) body.quantity = quantity;
    const { data } = await api.post("/zpl/generate", body);
    return data; // { zpl, variables, labels, pages }
}

export async function exportPrn(design, substitutions = null, filename = "etiqueta.prn") {
    const res = await api.post(
        "/zpl/export",
        { design, substitutions },
        { responseType: "blob" }
    );
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

/**
 * Las vistas previas se piden como imagen (blob): si fallan, el mensaje del servidor llega dentro de
 * un blob. Lo leemos para mostrar algo entendible ("este .prn no tiene etiquetas") en vez de un
 * mensaje genérico.
 */
async function previewError(error) {
    let detail = "";
    try {
        const data = error?.response?.data;
        if (data instanceof Blob) detail = JSON.parse(await data.text())?.detail || "";
    } catch {
        /* sin detalle */
    }
    const err = new Error(detail || "No se pudo generar la vista previa");
    err.userMessage = detail || "";
    return err;
}

export async function previewZpl(design, substitutions = null) {
    try {
        const res = await api.post("/zpl/preview", { design, substitutions }, { responseType: "blob" });
        return URL.createObjectURL(res.data);
    } catch (e) {
        throw await previewError(e);
    }
}

export async function listTemplates() {
    const { data } = await api.get("/templates");
    return data;
}

export async function getTemplate(id) {
    const { data } = await api.get(`/templates/${id}`);
    return data;
}

export async function saveTemplate({ name, design, notes }) {
    const { data } = await api.post("/templates", { name, design, notes });
    return data;
}

export async function updateTemplate(id, payload) {
    const { data } = await api.put(`/templates/${id}`, payload);
    return data;
}

export async function deleteTemplate(id) {
    await api.delete(`/templates/${id}`);
}

export async function duplicateTemplate(id) {
    const { data } = await api.post(`/templates/${id}/duplicate`);
    return data;
}

export async function parseBatch(file) {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/batch/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
}

export async function uploadImage(file) {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/image/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data; // { id, width, height, ext }
}

export function imageThumbnailUrl(imageId) {
    return `${API}/image/${imageId}/thumbnail`;
}

export async function importPrnTemplate(file, name) {
    const fd = new FormData();
    fd.append("file", file);
    if (name) fd.append("name", name);
    const { data } = await api.post("/templates/import-prn", fd, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
}

export async function rawVariables(zpl) {
    const { data } = await api.post("/raw/variables", { zpl });
    return data.variables;
}

export async function rawPreview(zpl, substitutions) {
    try {
        const res = await api.post("/raw/preview", { zpl, substitutions }, { responseType: "blob" });
        return URL.createObjectURL(res.data);
    } catch (e) {
        throw await previewError(e);
    }
}

/** Informa un error de la interfaz al servidor (sin molestar al usuario si falla). */
export function reportClientError({ message, stack, componentStack, kind }) {
    try {
        api.post("/client-errors", {
            message: String(message || "error").slice(0, 2000),
            stack: stack ? String(stack).slice(0, 8000) : undefined,
            componentStack: componentStack ? String(componentStack).slice(0, 4000) : undefined,
            url: window.location.href.slice(0, 500),
            userAgent: navigator.userAgent.slice(0, 400),
            kind,
        }).catch(() => {});
    } catch {
        /* nada */
    }
}

/** Columnas (etiquetas por hoja) detectadas en un .prn importado. */
export async function rawAnalyze(zpl, columns = null) {
    const body = { zpl };
    if (columns) body.columns = columns;
    const { data } = await api.post("/raw/analyze", body);
    return data; // { columns, variables, notes }
}

/** ZPL de un .prn con cantidad exacta, como texto listo para el agente de impresion. */
export async function rawZplText(zpl, substitutions, quantity = null, columns = null) {
    const body = { zpl, substitutions };
    if (quantity) body.quantity = quantity;
    if (columns) body.columns = columns;
    const res = await api.post("/raw/export", body, { responseType: "text", transformResponse: (d) => d });
    return {
        zpl: res.data,
        labels: Number(res.headers["x-total-labels"]) || null,
        pages: Number(res.headers["x-total-pages"]) || null,
    };
}

export async function rawExport(zpl, substitutions, filename = "etiqueta.prn") {
    const res = await api.post(
        "/raw/export",
        { zpl, substitutions },
        { responseType: "blob" }
    );
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function saveBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

/**
 * Lote desde un .prn. `quantityColumn` = columna con la cantidad de ETIQUETAS por fila.
 * opts.mix: etiquetas de filas distintas comparten hoja (defecto: no; cada fila empieza en hoja nueva).
 * opts.columns: columnas del papel (defecto: autodetectar).
 */
function rawBatchBody(zpl, rows, mapping, quantityColumn, opts = {}) {
    const body = { zpl, rows, mapping, quantityColumn, mix: opts.mix === true };
    if (opts.columns) body.columns = opts.columns;
    return body;
}

export async function rawBatch(zpl, rows, mapping, quantityColumn, filename = "lote.prn", opts = {}) {
    const res = await api.post("/raw/batch", rawBatchBody(zpl, rows, mapping, quantityColumn, opts), {
        responseType: "blob",
    });
    saveBlob(res.data, filename);
    return res.headers["x-total-labels"];
}

/** Igual que rawBatch pero devuelve el ZPL en vez de descargarlo (1 sola llamada para todo el lote). */
export async function rawBatchText(zpl, rows, mapping, quantityColumn, opts = {}) {
    const res = await api.post("/raw/batch", rawBatchBody(zpl, rows, mapping, quantityColumn, opts), {
        responseType: "text",
        transformResponse: (d) => d,
    });
    return {
        zpl: res.data,
        labels: Number(res.headers["x-total-labels"]),
        pages: Number(res.headers["x-total-pages"]),
    };
}

export async function generateBatch(design, rows, mapping, quantityColumn, opts = {}) {
    const res = await api.post(
        "/batch/generate",
        { design, rows, mapping, quantityColumn, mix: opts.mix === true },
        { responseType: "blob" }
    );
    saveBlob(res.data, "lote.prn");
    return res.headers["x-total-labels"];
}

export async function generateBatchText(design, rows, mapping, quantityColumn, opts = {}) {
    const res = await api.post(
        "/batch/generate",
        { design, rows, mapping, quantityColumn, mix: opts.mix === true },
        { responseType: "text", transformResponse: (d) => d }
    );
    return {
        zpl: res.data,
        labels: Number(res.headers["x-total-labels"]),
        pages: Number(res.headers["x-total-pages"]),
    };
}

/** Etiquetas y hojas de un lote, para mostrarlo antes de imprimir (misma regla que el backend). */
export function batchTotals(rows, quantityColumn, capacity, mix = false) {
    const qtys = rows.map((row) => {
        const v = quantityColumn ? parseInt(String(row[quantityColumn] ?? "1"), 10) : 1;
        return Number.isFinite(v) && v > 0 ? v : 1;
    });
    const labels = qtys.reduce((a, b) => a + b, 0);
    const cap = Math.max(1, capacity || 1);
    const pages = mix ? Math.ceil(labels / cap) : qtys.reduce((a, q) => a + Math.ceil(q / cap), 0);
    return { labels, pages };
}
